import { ShieldCheck } from "lucide-react";
import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/sections/PageHero";
import { SectionIntro } from "@/components/sections/SectionIntro";
import { OfferingGrid } from "@/components/sections/OfferingGrid";
import { ImageFeature } from "@/components/sections/ImageFeature";
import { CtaBand } from "@/components/sections/CtaBand";
import { Reveal } from "@/components/motion/Reveal";
import { CONSULT } from "@/content/offerings";
import { enquire } from "@/content/navigation";

export const ConsultDisclaimer = () => (
  <Reveal className="mt-12 flex gap-4 rounded-2xl border border-gold/40 bg-gold/10 p-6 sm:p-8" data-testid="consult-disclaimer">
    <ShieldCheck className="h-6 w-6 shrink-0 text-gold-light" strokeWidth={1.4} aria-hidden="true" />
    <p className="text-[15px] leading-relaxed text-white/85">
      <strong className="font-semibold text-white">Please note:</strong> VION Consult provides guidance, advisory and preparation support. We do not guarantee visa approvals, immigration outcomes, admissions or employment. Decisions rest with the relevant institutions, employers and authorities, and outcomes vary based on individual circumstances and applicable requirements.
    </p>
  </Reveal>
);

export default function Consult() {
  const speak = enquire("VION Consult");
  return (
    <>
      <Seo title="VION Consult | Career, International Education & Global Opportunities" description="VION Consult offers guidance on career development, international education, study abroad, admissions support, travel and relocation consulting." path="/vion-consult" />
      <PageHero
        testid="consult-hero"
        eyebrow="VION Consult"
        lines={["Your Pathway to Career,", "Education and", "Global Opportunities."]}
        accentLine={2}
        intro="Helping individuals navigate career, education, travel, relocation and global opportunities with clear, honest guidance."
        image="/images/consult-hero.webp"
        alt="African graduate smiling in academic gown at a graduation ceremony"
        shape="pill"
        actions={[
          { label: "Speak With a Consultant", to: speak, testid: "consult-speak-btn" },
          { label: "Get Started", to: "/contact", variant: "outline", testid: "consult-get-started-btn" },
        ]}
      />
      <section className="bg-ink py-24 sm:py-32" data-testid="consult-services">
        <div className="shell">
          <SectionIntro eyebrow="Services" title={<>Guidance for every step <span className="italic text-gold-metal">beyond borders.</span></>} />
          <div className="mt-16">
            <OfferingGrid items={CONSULT} prefix="consult" ctaLabel="Speak With a Consultant" ctaHref={(c) => enquire("VION Consult", c.title)} />
          </div>
          <ConsultDisclaimer />
        </div>
      </section>
      <ImageFeature
        testid="consult-feature"
        image="/images/consult-2.webp"
        alt="Two travellers walking through a bright international airport terminal"
        eyebrow="Global Mobility"
        title="Plan your next move with confidence."
        text={["From choosing a study destination to preparing for relocation, we help you understand your options and prepare thoroughly.", "Our role is to guide and support — so you can make informed decisions about your future."]}
        actions={[{ label: "Speak With a Consultant", to: speak, testid: "consult-feature-speak-btn" }]}
      />
      <CtaBand testid="consult-cta" eyebrow="VION Consult" title="Let's map your pathway." text="Share your goals and a consultant will get in touch to discuss your options." primary={{ label: "Speak With a Consultant", to: speak }} />
    </>
  );
}
